// Pure Analytics export builders — no React/DOM/fetch. They take the already-pure `reportData`
// model (built in Analytics.jsx from authoritative tenant metrics) plus the export lib, and return
// the file content. This makes the CSV/XLSX/PDF output Node-testable (analyticsExport.test.js)
// without jsdom. The visible Analytics export handler passes jsPDF/XLSX into these, so production
// output is unchanged. Values are never fabricated — missing metrics arrive as 'N/A' from the model.

export function buildAnalyticsCsvText(data) {
  const headers = ['Category', 'Count', 'Percentage', 'Status'];
  const rows = (data.metrics?.ticketsByCategory ?? []).map((c) => [c.name, c.count, `${c.value}%`, 'Active']);
  return [headers, ...rows]
    .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    .join('\n');
}

export function buildAnalyticsWorkbookModel(data, generatedLabel = '') {
  return {
    'Executive Summary': [
      ['Analytics Report - ' + data.period.toUpperCase()],
      ['Generated:', generatedLabel],
      [''],
      ['Executive Summary'],
      ['Metric', 'Value'],
      ['Total Tickets', data.summary.totalTickets],
      ['Resolved Tickets', data.summary.resolvedTickets],
      ['Avg Resolution Time', data.summary.avgResolutionTime],
      ['Customer Satisfaction', data.summary.customerSatisfaction],
      ['Auto-Resolution Rate', data.summary.autoResolutionRate],
      ['Escalation Rate', data.summary.escalationRate],
      [''],
      ['Performance Metrics'],
      ['Avg First Response', data.performance.avgFirstResponse],
      ['Avg Chat Duration', data.performance.avgChatDuration],
      ['Callback Rate', data.performance.callbackRate],
      ['Reopened Tickets', data.performance.reopenedTickets],
      ['OpenAI Usage Rate', data.performance.openaiUsageRate],
    ],
    Categories: [
      ['Category Analysis'],
      ['Category', 'Count', 'Percentage'],
      ...(data.metrics?.ticketsByCategory ?? []).map((c) => [c.name, c.count, `${c.value}%`]),
    ],
    Intents: [
      ['Conversation Intents'],
      ['Intent', 'Count'],
      ...(data.metrics?.intentDistribution ?? []).map((it) => [it.name, it.count]),
    ],
  };
}

export function writeAnalyticsWorkbook(data, XLSX, generatedLabel = '') {
  const model = buildAnalyticsWorkbookModel(data, generatedLabel);
  const wb = XLSX.utils.book_new();
  for (const [name, aoa] of Object.entries(model)) {
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(aoa), name);
  }
  return XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
}

export function buildAnalyticsPdfModel(data, generatedLabel = '') {
  return {
    title: 'Board-Ready Analytics Report',
    section: data.section,
    generatedLabel,
    period: data.period.toUpperCase(),
    summary: [
      { label: 'Total Tickets', value: data.summary.totalTickets },
      { label: 'Avg Resolution Time', value: data.summary.avgResolutionTime },
      { label: 'Customer Satisfaction', value: data.summary.customerSatisfaction },
      { label: 'Auto-Resolution Rate', value: data.summary.autoResolutionRate },
    ],
    categories: {
      headers: ['Category', 'Count', 'Percentage'],
      rows: (data.metrics?.ticketsByCategory ?? []).map((c) => [c.name, String(c.count), `${c.value}%`]),
    },
    performance: [
      { label: 'Avg First Response', value: data.performance.avgFirstResponse },
      { label: 'Avg Chat Duration', value: data.performance.avgChatDuration },
      { label: 'Callback Rate', value: data.performance.callbackRate },
      { label: 'Reopened Tickets', value: String(data.performance.reopenedTickets) },
      { label: 'OpenAI Usage Rate', value: data.performance.openaiUsageRate },
      { label: 'Escalation Rate', value: data.summary.escalationRate },
    ],
  };
}

export function renderAnalyticsPdf(data, jsPDFCtor, generatedLabel = '') {
  const m = buildAnalyticsPdfModel(data, generatedLabel);
  const pdf = new jsPDFCtor();
  pdf.setFontSize(20); pdf.setTextColor(59, 130, 246); pdf.text(m.title, 20, 30);
  pdf.setFontSize(12); pdf.setTextColor(0, 0, 0);
  pdf.text(`Generated: ${m.generatedLabel}`, 20, 45);
  pdf.text(`Period: ${m.period}`, 20, 55);
  pdf.setFontSize(16); pdf.setTextColor(139, 92, 246); pdf.text('Executive Summary', 20, 75);
  pdf.setFontSize(12); pdf.setTextColor(0, 0, 0);
  m.summary.forEach((s, i) => pdf.text(`${s.label}: ${s.value}`, 20, 90 + i * 10));
  let y = 150;
  pdf.setFontSize(16); pdf.setTextColor(139, 92, 246); pdf.text('Ticket Analysis by Category', 20, y);
  y += 15; pdf.setFontSize(12); pdf.setTextColor(0, 0, 0);
  pdf.text(m.categories.headers[0], 20, y); pdf.text(m.categories.headers[1], 80, y); pdf.text(m.categories.headers[2], 120, y);
  y += 10;
  m.categories.rows.forEach((r, i) => { const ry = y + i * 8; pdf.text(r[0], 20, ry); pdf.text(r[1], 80, ry); pdf.text(r[2], 120, ry); });
  pdf.addPage();
  pdf.setFontSize(16); pdf.setTextColor(139, 92, 246); pdf.text('Performance Metrics', 20, 30);
  pdf.setFontSize(12); pdf.setTextColor(0, 0, 0);
  m.performance.forEach((p, i) => pdf.text(`${p.label}: ${p.value}`, 20, 50 + i * 10));
  return pdf.output('arraybuffer');
}
